import CrcCheckboxCheck from "@/svg/CrcCheckboxCheck";
import { ChangeEvent, useId, useState } from "react";

const CHECKBOX_SIZE_CLASS = "h-[14px] w-[14px]";
const CHECKBOX_RADIUS_CLASS = "rounded-[2.91667px]";
const CHECKBOX_BORDER_CLASS = "border-[0.833px]";

/**
 * Props for a CRC recommended-action row with a Figma-styled checkbox.
 */
export interface CrcActionCardProps {
    title: string;
    subtitle: string;
    meta: string;
    checked?: boolean;
    defaultChecked?: boolean;
    disabled?: boolean;
    id?: string;
    name?: string;
    onCheckedChange?: (checked: boolean) => void;
}

/**
 * Recommended CRC action row: custom 14×14 checkbox plus titled action copy.
 *
 * @param props - Action copy and checkbox state
 * @param props.title - Primary action label (e.g. lab name)
 * @param props.subtitle - Secondary line under the title
 * @param props.meta - Italic detail line (e.g. latest result)
 * @param props.checked - Controlled checked state; omit for uncontrolled
 * @param props.defaultChecked - Initial checked state when uncontrolled
 * @param props.disabled - When true, the control cannot be toggled
 * @param props.id - Optional id for the native checkbox input
 * @param props.name - Optional form name for the native checkbox input
 * @param props.onCheckedChange - Called with the next checked value after a toggle
 * @returns Action row with a custom checkbox
 */
const CrcActionCard = ({
    title,
    subtitle,
    meta,
    checked,
    defaultChecked = false,
    disabled = false,
    id,
    name,
    onCheckedChange,
}: CrcActionCardProps) => {
    const generatedId = useId();
    const inputId = id ?? generatedId;
    const isControlled = checked !== undefined;
    const [uncontrolledChecked, setUncontrolledChecked] = useState(defaultChecked);
    const isChecked = isControlled ? checked : uncontrolledChecked;

    /**
     * Syncs checkbox state and notifies the parent of the next value.
     *
     * @param event - Native change event from the hidden checkbox
     */
    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const nextChecked = event.target.checked;
        if (!isControlled) {
            setUncontrolledChecked(nextChecked);
        }
        onCheckedChange?.(nextChecked);
    };

    return (
        <label
            htmlFor={inputId}
            className={`crc-action-card--container flex gap-4 items-center ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                }`}
        >
            <span className={`relative mt-0.5 shrink-0 ${CHECKBOX_SIZE_CLASS}`}>
                <input
                    id={inputId}
                    name={name}
                    type="checkbox"
                    checked={isChecked}
                    disabled={disabled}
                    onChange={handleChange}
                    className="peer sr-only"
                />
                <span
                    className={`pointer-events-none absolute inset-0 box-border ${CHECKBOX_SIZE_CLASS} ${CHECKBOX_RADIUS_CLASS} ${CHECKBOX_BORDER_CLASS} border-primaryGray-4 bg-white peer-checked:hidden`}
                />
                <span
                    className={`pointer-events-none absolute inset-0 hidden overflow-hidden box-border ${CHECKBOX_SIZE_CLASS} ${CHECKBOX_RADIUS_CLASS} ${CHECKBOX_BORDER_CLASS} border-primaryGray-1 bg-primaryGray-1 peer-checked:block`}
                >
                    <CrcCheckboxCheck />
                </span>
            </span>
            <div className="description flex-1 min-w-0 text-xs font-normal text-primaryGray-4 leading-[1.125rem] tracking-tightest p-3 rounded-md border border-primaryGray-14 py-3 px-2 hover:border-2 transition-all">
                <div className="description-section flex-1 min-w-0 flex flex-col gap-[0.125rem]">
                    <div className="description-section--title text-xs font-semibold text-primaryGray-1">{title}</div>
                    <div className="description-section--value text-x-tiny font-normal text-primaryGray-4 leading-4">{subtitle}</div>
                    <div className="description-section--meta text-x-tiny font-normal text-primaryGray-6 leading-4 italic">{meta}</div>
                </div>
            </div>
        </label>
    );
};

export default CrcActionCard;
